import Constant from "./Constant";
import store from "./store";
import {FluxEvent, Permission} from "./Enums";
import Dispatcher from "./dispatcher";
const axios = require('axios');

class ULPSPortalApiClient {
  constructor(apiUrl) {
    this.apiUrl = apiUrl;
  }

  updateAuthHeader() {
    if (store.token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${store.token}`;
    }
    else {
      axios.defaults.headers.common = {};
    }
  }

  async login(login, password) {
    try {
      const response = await axios.post(`${this.apiUrl}/login`, {login, password} ,{ withCredentials: true });
      store.token = response.data.accessToken
      store.tokenExp = response.data.expiresAt
      localStorage.setItem("tokenExp", store.tokenExp);
      return true;
    } catch (e) {
      if(e.response?.text === "[400] Already logged in") {
        await this.logout();
        return await this.login();
      }
      ULPSPortalApiClient.handleError(e)
    }
  }

async refreshToken() {
  try {
    const response = await axios.post(
      `${this.apiUrl}/refresh`,
      {},
      { withCredentials: true }
    );

    store.token = response.data.accessToken;
    store.tokenExp = response.data.expiresAt;

    this.updateAuthHeader();
    console.log("✅ Token refreshed successfully");

    return true;
  } catch (e) {

    if (e.response?.status === 401) {
      console.log("No active session.");
    } else {
      console.error("❌ Refresh failed", e?.response || e);
    }

    store.token = null;
    store.tokenExp = null;

    return false;
  }
}


  async logout() {
    window.location.href="#"
    sessionStorage.clear();
    return await axios.post(`${this.apiUrl}/logout`).then( _ => {
      store.tokenExp = null;
      store.token = "";
      return true;
    });
  }

  async getUser() {
    if(store.isTokenExpired()){
      this.handleTokenExpiration()
    }
    try {
      const response = await axios.get(`${this.apiUrl}/user`);
      if (response.data !== null) {
        const data = response.data;
        const isAdmin = data.isAdmin;
        const isDelivery = data.isDelivery;
        const isSupport = data.isSupport;
        const distributorId = data.distributorId;
        store.user = response.data;
        const permissions = [
          isAdmin && Permission.ADMIN,
          isDelivery && Permission.DELIVERY,
          isSupport && Permission.SUPPORT,
          distributorId && Permission.DISTRIBUTOR
        ].filter(e=>e);
        store.session = {permissions};
        Dispatcher.dispatch({contentType: FluxEvent.SESSION_CHANGED});
        Dispatcher.dispatch({contentType: FluxEvent.PERMISSIONS_CHANGED});
        Dispatcher.dispatch({contentType: FluxEvent.USER_CHANGED});
        return response;
      } else {
        this.logout().then( _ => {
          window.location.reload();
        })
      }
    } catch (err) {
      ULPSPortalApiClient.handleError(err)
    }
  }

  async getUsers() {
    if(store.isTokenExpired()){
      const accessTokenIsValid = await this.ensureValidAccessToken()
      if(!accessTokenIsValid)
        return;
    }
    try {
      const response = await axios.get(`${this.apiUrl}/users`);
      if (response.data !== null) {
        return response;
      } else {
        this.logout().then( _ => {
          window.location.reload();
        })
      }
    } catch (err) {
      ULPSPortalApiClient.handleError(err)
    }
  }

  async getFeature(featureId) {
    if(store.isTokenExpired()){
      const accessTokenIsValid = await this.ensureValidAccessToken()
      if(!accessTokenIsValid)
        return;
    }
    try {
      const response = await axios.get(`${this.apiUrl}/features/${featureId}`);
      return response.data;
    } catch (err) {
      ULPSPortalApiClient.handleError(err)
    }
  }

  async createFeature(payload) {
    if(store.isTokenExpired()){
      const accessTokenIsValid = await this.ensureValidAccessToken()
      if(!accessTokenIsValid)
        return;
    }
    try {
      const response = await axios.post(`${this.apiUrl}/features`, payload);
      return response.data;
    } catch (err) {
      ULPSPortalApiClient.handleError(err)
    }
  }

  async getDistributors() {
    if(store.isTokenExpired()){
      const accessTokenIsValid = await this.ensureValidAccessToken()
      if(!accessTokenIsValid)
        return;
    }
    try {
      const response = await axios.get(`${this.apiUrl}/distributors`);
      return response.data;
    } catch (err) {
      ULPSPortalApiClient.handleError(err)
    }
  }

  async getDistributorDetails(distributorId) {
    if(store.isTokenExpired()){
      const accessTokenIsValid = await this.ensureValidAccessToken()
      if(!accessTokenIsValid)
        return;
    }
    if (!distributorId) return;
    try {
      const response = await axios.get(`${this.apiUrl}/distributors/${distributorId}`);
      return response.data;
    } catch (err) {
      ULPSPortalApiClient.handleError(err)
    }
  }

  async generateLicences(distributorId, payload) {
    if(store.isTokenExpired()){
      const accessTokenIsValid = await this.ensureValidAccessToken()
      if(!accessTokenIsValid)
        return;
    }
    try {
      const response = await axios.post(`${this.apiUrl}/distributors/${distributorId}/generateLicences`, payload);
      return response.data;
    } catch (err) {
      ULPSPortalApiClient.handleError(err)
    }
  }

  async getFreshBatches(distributorId) {
    if(store.isTokenExpired()){
      const accessTokenIsValid = await this.ensureValidAccessToken()
      if(!accessTokenIsValid)
        return;
    }
    try {
      const response = await axios.get(`${this.apiUrl}/distributors/${distributorId}/batches/fresh`);
      return response.data;
    } catch (err) {
      ULPSPortalApiClient.handleError(err)
    }
  }

  async getOldBatches(distributorId) {
    if(store.isTokenExpired()){
      const accessTokenIsValid = await this.ensureValidAccessToken()
      if(!accessTokenIsValid)
        return;
    }
    try {
      const response = await axios.get(`${this.apiUrl}/distributors/${distributorId}/batches/old`);
      return response.data;
    } catch (err) {
      ULPSPortalApiClient.handleError(err)
    }
  }

  async getLicences(
    substr,
    useActivationKey = true,
    useHardwareId = true,
    useCustomerInfo = true,
    usePurchaseOrderId = true,
    showUnused = false,
    showDisabled = false,
    useComment = false,
    licenceSchema
  ) {
    if(store.isTokenExpired()){
      const accessTokenIsValid = await this.ensureValidAccessToken()
      if(!accessTokenIsValid)
        return;
    }
    try {
      const response = await axios.get(`${this.apiUrl}/licences`, {
        params: {
          substr: substr,
          use_activation_key: useActivationKey,
          use_hardware_id: useHardwareId,
          use_customer_info: useCustomerInfo,
          use_purchase_order_id: usePurchaseOrderId,
          show_unused: showUnused,
          show_disabled: showDisabled,
          use_comment: useComment,
          licence_schema: licenceSchema
        },
      });
      return response.data;
    } catch (err) {
      ULPSPortalApiClient.handleError(err);
    }
  }

  async getLicence(licenceId) {
    if(store.isTokenExpired()){
      const accessTokenIsValid = await this.ensureValidAccessToken()
      if(!accessTokenIsValid)
        return;
    }
    try {
      const response = await axios.get(`${this.apiUrl}/licences/${licenceId}`);
      return response.data;
    } catch (err) {
      ULPSPortalApiClient.handleError(err)
    }
  }

  async getFeatures(licenceSchema) {
    if(store.isTokenExpired()){
      const accessTokenIsValid = await this.ensureValidAccessToken()
      if(!accessTokenIsValid)
        return;
    }
    try {
      const response = await axios.get(`${this.apiUrl}/features`, {
        params: licenceSchema ? { licence_schema: licenceSchema } : {},
      });
      return response.data;
    } catch (err) {
      ULPSPortalApiClient.handleError(err)
    }
  }

  async getFeatureGroups(licenceSchema) {
    if(store.isTokenExpired()){
      const accessTokenIsValid = await this.ensureValidAccessToken()
      if(!accessTokenIsValid)
        return;
    }
    try {
      const response = await axios.get(`${this.apiUrl}/feature-groups`, {
        params: licenceSchema ? { licence_schema: licenceSchema } : {},
      });
      return response.data;
    } catch (err) {
      ULPSPortalApiClient.handleError(err)
    }
  }

  async getFeatureGroup(featureGroupId) {
    if(store.isTokenExpired()){
      const accessTokenIsValid = await this.ensureValidAccessToken()
      if(!accessTokenIsValid)
        return;
    }
    try {
      const response = await axios.get(`${this.apiUrl}/feature-groups/${featureGroupId}`);
      return response.data;
    } catch (err) {
      ULPSPortalApiClient.handleError(err)
    }
  }

  async createFeatureGroup(payload) {
    if(store.isTokenExpired()){
      const accessTokenIsValid = await this.ensureValidAccessToken()
      if(!accessTokenIsValid)
        return;
    }
    try {
      const response = await axios.post(`${this.apiUrl}/feature-groups`, payload);
      return response.data;
    } catch (err) {
      ULPSPortalApiClient.handleError(err)
    }
  }

  async updateFeatureGroup(featureGroupId, payload) {
    if(store.isTokenExpired()){
      const accessTokenIsValid = await this.ensureValidAccessToken()
      if(!accessTokenIsValid)
        return;
    }
    try {
      const response = await axios.put(`${this.apiUrl}/feature-groups/${featureGroupId}`, payload);
      return response.data;
    } catch (err) {
      ULPSPortalApiClient.handleError(err)
    }
  }

  async deleteFeatureGroup(featureGroupId) {
    if(store.isTokenExpired()){
      const accessTokenIsValid = await this.ensureValidAccessToken()
      if(!accessTokenIsValid)
        return;
    }
    try {
      await axios.delete(`${this.apiUrl}/feature-groups/${featureGroupId}`);
      return true;
    } catch (err) {
      ULPSPortalApiClient.handleError(err)
    }
  }

  async deleteFeature(id) {
    if(store.isTokenExpired()){
      const accessTokenIsValid = await this.ensureValidAccessToken()
      if(!accessTokenIsValid)
        return;
    }
    try {
      await axios.delete(`${this.apiUrl}/features/${id}`);
      return true;
      
    } catch (err) {
      ULPSPortalApiClient.handleError(err)
    }
  }

  async resetLicence(licenceId) {
    if(store.isTokenExpired()){
      const accessTokenIsValid = await this.ensureValidAccessToken()
      if(!accessTokenIsValid)
        return;
    }
    try {
      const response = await axios.post(`${this.apiUrl}/licences/${licenceId}/reset`);
      return response.data;
    } catch (err) {
      ULPSPortalApiClient.handleError(err)
    }
  }

  async disableLicence(licenceId) {
    if(store.isTokenExpired()){
      const accessTokenIsValid = await this.ensureValidAccessToken()
      if(!accessTokenIsValid)
        return;
    }
    try {
      const response = await axios.post(`${this.apiUrl}/licences/${licenceId}/disable`);
      return response.data;
    } catch (err) {
      ULPSPortalApiClient.handleError(err)
    }
  }

async enableLicence(licenceId, hardwareIds) {
    if(store.isTokenExpired()){
      const accessTokenIsValid = await this.ensureValidAccessToken()
      if(!accessTokenIsValid)
        return;
    }
  try {
    const response = await axios.post(
      `${this.apiUrl}/licences/${licenceId}/enable`,
      { hardwareIds }
    );
    return response.data;
  } catch (err) {
    ULPSPortalApiClient.handleError(err);
  }
}

  async getBatchDetails(distributorId, batchNumber) {
    if(store.isTokenExpired()){
      const accessTokenIsValid = await this.ensureValidAccessToken()
      if(!accessTokenIsValid)
        return;
    }
    if (!distributorId || !batchNumber) return;
    try {
      const response = await axios.get(`${this.apiUrl}/distributors/${distributorId}/batch/${batchNumber}`);
      return response.data;
    } catch (err) {
      ULPSPortalApiClient.handleError(err)
    }
  }

  async downloadBatch(distributorId, batchNumber) {
    if(store.isTokenExpired()){
      const accessTokenIsValid = await this.ensureValidAccessToken()
      if(!accessTokenIsValid)
        return;
    }
    try {
      const response = await axios.get(`${this.apiUrl}/distributors/${distributorId}/batch/${batchNumber}/download`);
      return response.data;
    } catch (err) {
      ULPSPortalApiClient.handleError(err)
    }
  }

  async downloadSingeLicence(licenceId) {
    if(store.isTokenExpired()){
      const accessTokenIsValid = await this.ensureValidAccessToken()
      if(!accessTokenIsValid)
        return;
    }
    try {
      const response = await axios.get(`${this.apiUrl}/licences/${licenceId}/download`);
      return response.data;
    } catch (err) {
      ULPSPortalApiClient.handleError(err)
    }
  }

  async updateLicence(licenceId, licence) {
    if(store.isTokenExpired()){
      const accessTokenIsValid = await this.ensureValidAccessToken()
      if(!accessTokenIsValid)
        return;
    }
    try {
      const response = await axios.put(`${this.apiUrl}/licences/${licenceId}`, licence);
      return response.data;
    } catch (err) {
      ULPSPortalApiClient.handleError(err)
    }
  }

  async getLicenceSchemas() {
    if(store.isTokenExpired()){
      const accessTokenIsValid = await this.ensureValidAccessToken()
      if(!accessTokenIsValid)
        return;
    }
    try {
      const response = await axios.get(`${this.apiUrl}/licenceSchema`);
      return response.data;
    } catch (err) {
      ULPSPortalApiClient.handleError(err);
    }
  }

  async ensureValidAccessToken() {
    const ok = await this.refreshToken();
    if (!ok) {
      store.session = null;
      sessionStorage.clear();
      localStorage.removeItem("tokenExp");
      window.location.href = "/";
      return false;
    }
    return true;
  }

  static handleError(error) {
    let message = null;
    let code = null;

    if (error.response) {
      code = error.response.status;
      const data = error.response.data;

      if (typeof data === "string") {
        message = data;
      } else if (data && typeof data.detail === "string") {
        message = data.detail;
      } else {
        message = JSON.stringify(data);
      }
    } else if (error.request) {
      code = "NO_RESPONSE";
      message = "No response received from server.";
    } else {
      code = "CLIENT_ERROR";
      message = error.message || "Unknown client error.";
    }

    throw new Error(`[${code}] ${message}`);
  }
}

const clientInstance = new ULPSPortalApiClient(Constant.apiUrl);
export default clientInstance
export {ULPSPortalApiClient as ULPSPortalApiClientClass};
